import { useEffect, useMemo, useState } from "react";
import { Plus, Trash, Tag, Truck } from "@phosphor-icons/react";
import { axleTemplatesApi } from "@/lib/api";

/**
 * Editor for per-asset axle override.
 * - When `axles` is null/empty → asset inherits the bound template (or company default).
 * - When `axles` is a non-empty list → it overrides everything for bridge-check.
 *
 * NOTE on row identity: incoming axles from the API don't carry client-side IDs,
 * so we mint stable `_key` values for every row on first render. Keys never
 * leave the editor — they're stripped from the payload before save.
 */
export default function AssetAxleEditor({ value, onChange, assetTemplateId }) {
  const [templates, setTemplates] = useState([]);
  const hasOverride = Array.isArray(value) && value.length > 0;

  // Ensure every row carries a stable `_key` so React doesn't reuse input DOM
  // nodes on row delete (which would steal focus / preserve stale values).
  const rows = useMemo(() => {
    if (!hasOverride) return [];
    return value.map((a, i) => (a && a._key ? a : { ...a, _key: `axle-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}` }));
  }, [value, hasOverride]);

  // Strip internal _key before sending up
  const emit = (next) => {
    if (next == null) return onChange(null);
    onChange(next.map(({ _key, ...rest }) => rest)); // eslint-disable-line no-unused-vars
  };

  useEffect(() => {
    axleTemplatesApi.list().then(setTemplates).catch((e) => {
      console.warn("axleTemplates list failed", e);
      setTemplates([]);
    });
  }, []);

  const seedFromTemplate = (tplId) => {
    const t = templates.find((x) => x.id === tplId);
    if (t) onChange(JSON.parse(JSON.stringify(t.axles)));
  };

  const setAxle = (i, key, v) => {
    const next = [...rows];
    next[i] = { ...next[i], [key]: v };
    emit(next);
  };

  const addAxle = () =>
    emit([
      ...rows,
      { label: `Axle ${rows.length + 1}`, capacity_lbs: 17000, spacing_ft: 4, _key: `axle-${Date.now()}-new-${Math.random().toString(36).slice(2, 8)}` },
    ]);

  const removeAxle = (i) => emit(rows.filter((_, idx) => idx !== i));

  return (
    <div className="md:col-span-2 border border-[#2a2a2a] bg-[#0d0d0d]" data-testid="asset-axle-editor">
      <div className="px-3 py-2 border-b border-[#2a2a2a] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck size={14} className="text-[#ff8c00]" weight="bold" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold">Axle Configuration</div>
            <div className="text-[10px] text-[#71717a] mt-0.5">
              {hasOverride
                ? "Per-asset override is ACTIVE — takes precedence over the template"
                : "Inheriting from template / company default"}
            </div>
          </div>
        </div>
        {hasOverride ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-[10px] uppercase tracking-[0.25em] text-[#ef4444] hover:bg-[#ef4444]/10 px-2 py-1 font-bold flex items-center gap-1"
            data-testid="axle-clear-override"
          >
            <Trash size={10} weight="bold" /> Clear override
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {templates.length > 0 && (
              <select
                defaultValue=""
                onChange={(e) => e.target.value && seedFromTemplate(e.target.value)}
                className="bg-[#1a1a1a] border border-[#2a2a2a] text-[10px] uppercase tracking-[0.2em] px-2 py-1 font-bold"
                data-testid="axle-seed-from"
              >
                <option value="">Seed from template…</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.axles.length}-axle)
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={() => onChange([{ label: "Steer", capacity_lbs: 12000, spacing_ft: 0 }])}
              className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] hover:bg-[#ff8c00]/10 px-2 py-1 font-bold flex items-center gap-1"
              data-testid="axle-start-override"
            >
              <Plus size={10} weight="bold" /> Override
            </button>
          </div>
        )}
      </div>

      {hasOverride && (
        <div className="p-3 space-y-2">
          <div className="grid grid-cols-[1fr_120px_120px_32px] gap-2 text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-bold px-1">
            <div>Label</div>
            <div>Capacity (lbs)</div>
            <div>Spacing (ft)</div>
            <div></div>
          </div>
          {rows.map((ax, i) => (
            <div key={ax._key} className="grid grid-cols-[1fr_120px_120px_32px] gap-2" data-testid={`axle-row-${i}`}>
              <input
                type="text"
                value={ax.label || ""}
                onChange={(e) => setAxle(i, "label", e.target.value)}
                className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1.5 text-xs font-mono"
                data-testid={`axle-label-${i}`}
              />
              <input
                type="number"
                value={ax.capacity_lbs}
                onChange={(e) => setAxle(i, "capacity_lbs", parseInt(e.target.value) || 0)}
                className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1.5 text-xs font-mono"
                data-testid={`axle-cap-${i}`}
              />
              <input
                type="number"
                step="0.5"
                value={ax.spacing_ft}
                onChange={(e) => setAxle(i, "spacing_ft", parseFloat(e.target.value) || 0)}
                disabled={i === 0}
                title={i === 0 ? "First axle has no preceding spacing" : ""}
                className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1.5 text-xs font-mono disabled:opacity-40"
                data-testid={`axle-spacing-${i}`}
              />
              <button
                type="button"
                onClick={() => removeAxle(i)}
                disabled={rows.length <= 1}
                className="text-[#ef4444] hover:bg-[#ef4444]/10 disabled:opacity-30 flex items-center justify-center"
                data-testid={`axle-remove-${i}`}
              >
                <Trash size={11} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addAxle}
            className="text-[10px] uppercase tracking-[0.25em] text-[#4682b4] hover:bg-[#4682b4]/10 px-2 py-1.5 font-bold flex items-center gap-1"
            data-testid="axle-add"
          >
            <Plus size={10} weight="bold" /> Add Axle
          </button>
          <div className="text-[10px] text-[#71717a] font-mono pt-1 flex items-center gap-1">
            <Tag size={10} /> Federal Bridge Formula evaluates every contiguous group of ≥2 axles against{" "}
            <code className="text-[#a1a1aa]">W = 500 × (LN/(N−1) + 12N + 36)</code>.
          </div>
        </div>
      )}
    </div>
  );
}
